import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserCog } from "lucide-react";
import api from "@/lib/api";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { StackedList, Pagination } from "@/components/shared/StackedList";
import { PageHeader, FilterBar } from "@/components/shared/PageChrome";
import { ViewTabs, SearchInput, InfoBanner, useListParams, ViewLink } from "@/components/shared/ListChrome";
import { LoadingState, ErrorState, EmptyState } from "@/components/shared/States";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";

const VIEWS = [{ value: "CURRENT", label: "Current" }, { value: "UPCOMING", label: "Upcoming" }, { value: "PAST", label: "Past" }, { value: "ALL", label: "All" }];
const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");
const initials = (n) => n.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");

export default function ClientPeople() {
  const { view, q, page, setView, setQ, setPage } = useListParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/client/people", { params: { view, q: q || undefined, page, page_size: 25 } }).then((r) => setData(r.data)).catch(() => setError(true));
  }, [view, q, page]);

  if (error) return <ErrorState testId="client-people-error" />;

  return (
    <div data-testid="client-people-page" className="space-y-5">
      <PageHeader title="People & Guests" subtitle="View the people from your organisation who are currently or have been accommodated." />

      <ViewTabs testId="client-people-views" tabs={VIEWS} value={view} onChange={setView} counts={data?.counts} />
      <FilterBar testId="client-people-filter-bar" summary={data ? `${data.total} ${data.total === 1 ? "person" : "people"} match` : null}>
        <SearchInput testId="client-people-search" value={q} onChange={setQ} placeholder="Search name or location…" />
      </FilterBar>

      {!data && <LoadingState testId="client-people-loading" />}
      {data && data.items.length === 0 && <EmptyState testId="client-people-empty" title={view === "ALL" && !q ? "No guests recorded yet" : "No people in this view"} description={view === "ALL" && !q ? "Guests are recorded against bookings by Jigsaw staff." : "Try another view or clear the search."} />}
      {data && data.items.length > 0 && (
        <div className="space-y-3">
          <StackedList testId="client-people-stacked" items={data.items} title={(g) => g.full_name} status={(g) => g.stay_status ? <StatusBadge status={g.stay_status} /> : null}
            onOpen={(g) => navigate(`/client/bookings/${g.booking_id}`)} canOpen={(g) => !!g.booking_id} openLabel="Open latest booking"
            fields={[{ label: "Location", value: (g) => g.location }, { label: "Booking", value: (g) => g.booking_reference, mono: true }, { label: "Check-in", value: (g) => fmt(g.check_in_date) }, { label: "Check-out", value: (g) => fmt(g.check_out_date) }]} />
          <div className="hidden sm:block">
          <Table data-testid="client-people-table">
            <TableHeader>
              <TableRow><TableHead>Name</TableHead><TableHead>Location</TableHead><TableHead>Property</TableHead><TableHead>Check-in</TableHead><TableHead>Check-out</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((g) => (
                <TableRow key={g.id} data-testid={`client-guest-row-${g.id}`}>
                  <TableCell>
                    <span className="flex items-center gap-2.5">
                      <span aria-hidden className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-jigsaw-navy/10 text-[11px] font-semibold text-jigsaw-navy">{initials(g.full_name)}</span>
                      <span className="font-medium text-jigsaw-navy">{g.full_name}</span>
                    </span>
                  </TableCell>
                  <TableCell>{g.location || "—"}</TableCell>
                  <TableCell>{g.property_name || "—"}</TableCell>
                  <TableCell className="whitespace-nowrap text-[13px]">{fmt(g.check_in_date)}</TableCell>
                  <TableCell className="whitespace-nowrap text-[13px]">{fmt(g.check_out_date)}</TableCell>
                  <TableCell>{g.stay_status ? <StatusBadge status={g.stay_status} /> : <span className="text-[12px] text-jigsaw-muted">No stay recorded</span>}</TableCell>
                  <TableCell className="text-right">{g.booking_id && <ViewLink testId={`client-guest-view-${g.id}`} onClick={() => navigate(`/client/bookings/${g.booking_id}`)} />}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          <Pagination testId="client-people-pagination" page={data.page} pageSize={data.page_size} total={data.total} onPage={setPage} />
        </div>
      )}

      <InfoBanner testId="client-people-banner" icon={UserCog} title="Need to update guest information?" text="Contact the Jigsaw team to request changes to guest details." actionLabel="Contact Jigsaw" to="/client/support?intent=guest-update" />
    </div>
  );
}
